import { v7 as uuidv7 } from 'uuid';
import { z } from 'zod';

/**
 * Where ids come from while a preview replays a change set: the server installs a source that hands out the
 * same ids every time the same plan is replayed (so a record opened in one request is found in the next).
 * `undefined` from the source falls back to the random ids below.
 */
export type IdSource = {
  uuid(): string | undefined;
  short(prefix: string, length: number): string | undefined;
};
let idSource: IdSource | null = null;

/** Install (or with `null` remove) the source the preview replay draws ids from. */
export function setIdSource(source: IdSource | null): void {
  idSource = source;
}

/** Time-ordered UUIDv7, used for every primary key. */
export function newId(): string {
  return idSource?.uuid() ?? uuidv7();
}

const ALPHABET = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

/** Short, random, prefixed identifier, e.g. `fld_7Hq2kDp9Za`. Used for field stable ids (JSONB keys). */
export function shortId(prefix: string, length = 10): string {
  const fixed = idSource?.short(prefix, length);
  if (fixed) return fixed;
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += ALPHABET[b % ALPHABET.length];
  return `${prefix}_${out}`;
}

export const Uuid = z.uuid();
export type Uuid = z.infer<typeof Uuid>;

/** Api keys of metadata elements: lower snake case, starts with a letter. */
export const ApiKey = z
  .string()
  .min(1)
  .max(63)
  .regex(/^[a-z][a-z0-9_]*$/, { error: 'Use lower_snake_case starting with a letter' });
export type ApiKey = z.infer<typeof ApiKey>;

/** Blueprint-scoped stable key, e.g. `crm:deals.stage`; customer additions use namespace `ws:`. */
export const StableKey = z
  .string()
  .min(3)
  .max(200)
  .regex(/^[a-z][a-z0-9_-]*:[a-zA-Z0-9_.:-]+$/, { error: 'Expected "<namespace>:<path>"' });
export type StableKey = z.infer<typeof StableKey>;
